import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CONFIGURE_PAGE_STATE } from '@occc-app/routes/AwardCatalog/types';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import { OfferFragment } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';

import { ConfirmDialog, Typography } from '@occc-app/design-system';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { AwardCatalogUpdatePage } from './AwardCatalogUpdatePage';

import { ConfigureFormContainer } from '../components/ConfigureForm/ConfigureFormContainer';
import { DesignFormContainer } from '../components/DesignForm/DesignFormContainer';
import { FinalizeFormContainer } from '../components/FinalizeForm/FinalizeFormContainer';
import { TargetFormContainer } from '../components/TargetForm/TargetFormContainer';
import { getIsTargetTabEnabled, routeCreator, useGetAwardType } from '../utils';
import { messages } from '../messages';

export const AwardCatalogUpdatePageContainer = () => {
  const chainId = useChainId();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: offerId = '' } = useParams<{ id: string }>();

  const { loading, data: award, refetch: refetchOffer } = useGetOfferById(offerId, chainId);

  const creatingState = (location.state as { creatingState?: CONFIGURE_PAGE_STATE })?.creatingState;
  const [pageState, setPageState] = useState(creatingState || CONFIGURE_PAGE_STATE.CONFIGURE);
  const { awardType, setAwardType } = useGetAwardType(award?.earningMechanism);

  const [isConfirmDialogOpened, setIsConfirmDialogOpened] = useState(false);
  const confirmCanceling = useCallback(() => navigate(routeCreator.base()), [navigate]);
  const cancel = useCallback(
    (hasUnsavedChanges: boolean) => {
      if (hasUnsavedChanges) {
        setIsConfirmDialogOpened(true);
        return;
      }
      confirmCanceling();
    },
    [confirmCanceling],
  );

  const formsMap = useMemo(() => {
    const map = new Map();

    map.set(CONFIGURE_PAGE_STATE.CONFIGURE, (newAward: OfferFragment) => {
      return (
        <ConfigureFormContainer
          award={newAward}
          refetchAward={refetchOffer}
          setPageState={setPageState}
          awardType={awardType}
          setAwardType={setAwardType}
          handleCancel={cancel}
          currentFlowType={creatingState ? Flow_Types.Configure : Flow_Types.Update}
        />
      );
    });

    if (getIsTargetTabEnabled(awardType)) {
      map.set(CONFIGURE_PAGE_STATE.TARGET, (newAward: OfferFragment) => {
        return (
          <TargetFormContainer
            award={newAward}
            refetchAward={refetchOffer}
            setPageState={setPageState}
            handleCancel={cancel}
            currentFlowType={creatingState ? Flow_Types.Configure : Flow_Types.Update}
          />
        );
      });
    }

    map.set(CONFIGURE_PAGE_STATE.DESIGN, (newAward: OfferFragment) => {
      return (
        <DesignFormContainer
          award={newAward}
          refetchAward={refetchOffer}
          setPageState={setPageState}
          handleCancel={cancel}
          currentFlowType={creatingState ? Flow_Types.Configure : Flow_Types.Update}
        />
      );
    });

    map.set(CONFIGURE_PAGE_STATE.FINALIZE, (newAward: OfferFragment) => {
      return (
        <FinalizeFormContainer
          award={newAward}
          handleCancel={cancel}
          currentFlowType={creatingState ? Flow_Types.Configure : Flow_Types.Update}
        />
      );
    });

    return map;
  }, [awardType, refetchOffer, setAwardType, cancel, creatingState]);

  const effectivePageState =
    !getIsTargetTabEnabled(awardType) && pageState === CONFIGURE_PAGE_STATE.TARGET
      ? CONFIGURE_PAGE_STATE.DESIGN
      : pageState;

  return (
    <>
      <AwardCatalogUpdatePage
        formsMap={formsMap}
        award={award}
        loading={loading}
        pageState={effectivePageState}
        awardType={awardType}
        setPageState={setPageState}
      />
      <ConfirmDialog
        dialogTitle={messages.cancelDialogHeader}
        open={isConfirmDialogOpened}
        declineButtonText='No'
        onClose={() => setIsConfirmDialogOpened(false)}
        confirmButtonText='Yes'
        confirmButtonProps={{
          onClick: confirmCanceling,
        }}
      >
        <Typography>{messages.cancelDialogDescription}</Typography>
      </ConfirmDialog>
    </>
  );
};
